/**
 * ProfileKeyPicker — searchable combobox over the network's cross-study field
 * tags (crm.profile_keys), replacing the free-text "profile key" inputs in the
 * form builder (medical_fact) and the prescreen criterion editor. Type to
 * filter by key or label; pick an existing tag, or register the typed text as
 * a NEW tag inline (dotted lowercase grammar) — so the vocabulary stays one
 * curated library instead of ad-hoc spellings per form.
 *
 * The full tag list is cached module-scoped for the session (the pickers live
 * inside modals that unmount on close — same pattern as FormAnswersFilter's
 * questions cache). The Field-tag library page busts the cache after edits.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';
import api from '../../../services/api';
import type { ProfileKey } from '../../types';
import { Z } from '../ui';

/** Creation grammar — mirrors backend POST /profile-keys (dotted lowercase). */
export const PROFILE_KEY_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)*$/;

let keysCache: ProfileKey[] | null = null;
let keysInflight: Promise<ProfileKey[]> | null = null;

/** Session-cached tag vocabulary (all statuses; consumers filter). */
export function loadProfileKeys(): Promise<ProfileKey[]> {
  if (keysCache) return Promise.resolve(keysCache);
  if (keysInflight) return keysInflight;
  keysInflight = api
    .get('/api/crm/profile-keys')
    .then((res) => {
      keysCache = (res.data?.profile_keys || []) as ProfileKey[];
      return keysCache;
    })
    .finally(() => {
      keysInflight = null;
    });
  return keysInflight;
}

/** Drop the session cache (library page calls this after any create/edit). */
export function bustProfileKeysCache(): void {
  keysCache = null;
}

/** Register a tag and keep the session cache coherent. */
export async function createProfileKey(body: {
  key: string;
  label: string;
  value_type?: string;
  unit?: string;
  description?: string;
}): Promise<ProfileKey> {
  const res = await api.post('/api/crm/profile-keys', body);
  const created = res.data?.profile_key as ProfileKey;
  if (keysCache && created) {
    keysCache = [...keysCache.filter((k) => k.key !== created.key), created].sort((a, b) =>
      a.key.localeCompare(b.key),
    );
  }
  return created;
}

/** Map a question/criterion answer type onto profile_keys.value_type. */
export function suggestValueTypeFor(answerType: string | undefined): string {
  switch (answerType) {
    case 'boolean':
    case 'yes_no':
      return 'boolean';
    case 'number':
    case 'scale':
    case 'calculation':
    case 'height':
      return 'number';
    case 'choice':
    case 'single_choice':
    case 'multiple_choice':
      return 'choice';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}

const TYPE_BADGE: Record<string, string> = {
  boolean: 'yes/no',
  number: 'number',
  choice: 'choice',
  text: 'text',
  date: 'date',
};

interface ProfileKeyPickerProps {
  /** Selected tag key ('' / null = none). */
  value: string | null;
  onChange: (key: string | null) => void;
  /** value_type used when registering a new tag from the search text. */
  suggestValueType?: string;
  /** Label used when registering a new tag (usually the question text). */
  suggestLabel?: string;
  /** Host form's input class so the trigger matches its siblings. */
  inputClassName?: string;
  placeholder?: string;
}

export default function ProfileKeyPicker({
  value,
  onChange,
  suggestValueType = 'text',
  suggestLabel = '',
  inputClassName = '',
  placeholder = 'Search tags… (e.g. condition.t2dm)',
}: ProfileKeyPickerProps) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<ProfileKey[] | null>(keysCache);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => (value ? (keys || []).find((k) => k.key === value) || null : null),
    [keys, value],
  );

  // Fetch once per session (or retry after a failure) when first opened.
  useEffect(() => {
    if (!open || keys || loading) return;
    setLoading(true);
    setLoadError(false);
    loadProfileKeys()
      .then((list) => setKeys(list))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [open, keys, loading]);

  // Close on outside mousedown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const term = search.trim().toLowerCase();
  // Archived tags are library history, not pickable vocabulary — except the
  // one already on this question, which must keep rendering.
  const pickable = useMemo(
    () => (keys || []).filter((k) => k.status !== 'archived' || k.key === value),
    [keys, value],
  );
  const matches = useMemo(() => {
    if (!term) return pickable;
    return pickable.filter(
      (k) => k.key.toLowerCase().includes(term) || k.label.toLowerCase().includes(term),
    );
  }, [pickable, term]);

  const exactExists = !!term && pickable.some((k) => k.key === term);
  const canCreate = !!term && !exactExists && PROFILE_KEY_RE.test(term) && term.length <= 120;
  // Options list the keyboard walks: matches then (optionally) the create row.
  const optionCount = matches.length + (canCreate ? 1 : 0);

  useEffect(() => {
    setHighlight(0);
  }, [term, open]);

  const pick = (key: string) => {
    onChange(key);
    setSearch('');
    setOpen(false);
  };

  const create = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createProfileKey({
        key: term,
        label: suggestLabel.trim().slice(0, 120) || term,
        value_type: suggestValueType,
      });
      setKeys((prev) => {
        const base = (prev || []).filter((k) => k.key !== created.key);
        return [...base, created].sort((a, b) => a.key.localeCompare(b.key));
      });
      pick(created.key);
    } catch (e: any) {
      setCreateError(e?.response?.data?.error || 'Could not register the tag');
    } finally {
      setCreating(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(optionCount - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < matches.length) {
        if (matches[highlight]) pick(matches[highlight].key);
      } else if (canCreate) {
        create();
      }
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-label="Field tag"
          value={open ? search : value || ''}
          placeholder={value ? value : placeholder}
          className={`${inputClassName} w-full ${value && !open ? 'font-mono' : ''} ${value ? 'pr-8' : ''}`}
          onFocus={() => {
            setOpen(true);
            setSearch('');
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setCreateError(null);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {value && !open && (
          <button
            type="button"
            aria-label="Clear tag"
            onClick={() => onChange(null)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 transition-colors duration-150 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {/* Selected-tag caption: the human label under the mono key. */}
      {selected && !open && (
        <p
          className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400"
          title={`${selected.key} — ${selected.label}${selected.status === 'draft' ? ' (draft tag)' : ''}`}
        >
          {selected.label}
          {selected.status === 'draft' ? ' · draft tag' : ''}
        </p>
      )}

      {open && (
        // Z.header, not Z.menu: this popover must clear the sticky builder chrome above it.
        <div className={`absolute ${Z.header} mt-1 w-max min-w-full max-w-[26rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800`}>
          <div className="max-h-56 overflow-y-auto py-1">
            {loading && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                Loading tags…
              </div>
            )}
            {loadError && !loading && (
              <button
                type="button"
                onClick={() => setKeys(null)}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-50 dark:text-red-400 dark:hover:bg-gray-700/60"
              >
                Couldn&rsquo;t load the tag library — tap to retry.
              </button>
            )}
            {!loading && !loadError && matches.length === 0 && !canCreate && (
              <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                {term
                  ? 'No matching tags. Tags are dotted lowercase (e.g. condition.t2dm).'
                  : 'No tags registered yet — type to create the first one.'}
              </p>
            )}
            {matches.map((k, i) => (
              <button
                key={k.id}
                type="button"
                onClick={() => pick(k.key)}
                onMouseEnter={() => setHighlight(i)}
                title={`${k.key} — ${k.label}${k.status === 'draft' ? ' (draft tag)' : ''}`}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-150 ${
                  i === highlight ? 'bg-gray-100 dark:bg-gray-700/60' : ''
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-xs text-gray-900 dark:text-gray-100">{k.key}</span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{k.label}</span>
                </span>
                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {TYPE_BADGE[k.value_type] || k.value_type}
                </span>
                {k.status === 'draft' && (
                  <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    draft
                  </span>
                )}
                {k.key === value && <Check className="h-3.5 w-3.5 shrink-0 text-primary-600 dark:text-primary-400" />}
              </button>
            ))}
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={create}
              onMouseEnter={() => setHighlight(matches.length)}
              disabled={creating}
              className={`flex w-full items-center gap-1.5 border-t border-gray-100 px-3 py-2 text-left text-sm font-medium text-primary-700 transition-colors duration-150 disabled:opacity-50 dark:border-gray-700 dark:text-primary-400 ${
                highlight === matches.length ? 'bg-primary-50 dark:bg-primary-900/20' : ''
              }`}
            >
              {creating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Register &ldquo;{term}&rdquo; as a new tag
            </button>
          )}
          {createError && (
            <p className="border-t border-gray-100 px-3 py-1.5 text-xs text-red-600 dark:border-gray-700 dark:text-red-400">
              {createError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
